import type { CoastalSceneState } from './coastal-types';
import { clampSurf, easeSurf, SURF_FACE_OFFSET, SURF_HEADING, SURF_LINEUP, surfCycleSeconds,
  surfSetAt, surfSetSurfacePoint, surfShorePoint, surfWaterPoint } from './surf-water';

export type WaterSceneSport = 'bodyboarding' | 'swim' | 'sup' | 'sup-touring' | 'sup-surf' | 'sup-downwind';
export const WATER_SCENE_SPORTS = {
  bodyboarding: { label: 'Bodyboarding', family: 'bodyboard' },
  swim: { label: 'Swimming', family: 'swim' },
  sup: { label: 'Paddleboarding', family: 'sup' },
  'sup-touring': { label: 'Touring SUP', family: 'sup' },
  'sup-surf': { label: 'SUP Surf', family: 'sup' },
  'sup-downwind': { label: 'Downwind SUP', family: 'sup' },
} as const;

export function isWaterSceneSport(value: unknown): value is WaterSceneSport {
  return typeof value === 'string' && Object.hasOwn(WATER_SCENE_SPORTS, value);
}

export function waterSportLabel(sport: WaterSceneSport): string {
  return WATER_SCENE_SPORTS[sport].label;
}

export type WaterSportSceneState = CoastalSceneState & {
  waterSportEvidence?: {
    currentSpeedMs: number | null;
    windDirectionKnown?: boolean;
  };
};

export type WaterSportClip = 'bodyboard-paddle' | 'bodyboard-ride' | 'bodyboard-turn' | 'bodyboard-trim'
  | 'swim-crawl' | 'swim-turn' | 'sup-paddle' | 'sup-kneel' | 'sup-brace' | 'sup-turn' | 'sup-trim' | 'shore';
export type WaterSportStage = 'resting' | 'waiting' | 'turning' | 'paddling' | 'riding' | 'swimming' | 'gliding';
export type WaterSportMotion = {
  clip: WaterSportClip;
  stage: WaterSportStage;
  pose01: number;
  stroke01: number;
  strokePower01: number;
  x: number;
  y: number;
  along: number;
  progress: number;
  heading: number;
  rotation: number;
  scale: number;
  opacity: number;
  speedPxS: number;
  riding: boolean;
  onWater: boolean;
  description: string;
  holdReason: string | null;
};

export function waterSportClips(sport: WaterSceneSport): readonly WaterSportClip[] {
  switch (WATER_SCENE_SPORTS[sport].family) {
    case 'bodyboard': return ['shore', 'bodyboard-paddle', 'bodyboard-ride', 'bodyboard-turn', 'bodyboard-trim'];
    case 'swim': return ['shore', 'swim-crawl', 'swim-turn'];
    case 'sup': return ['shore', 'sup-paddle', 'sup-kneel', 'sup-brace', 'sup-turn', 'sup-trim'];
  }
}

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const mix = (a: number, b: number, t: number) => a + (b - a) * t;
const fract = (value: number) => value - Math.floor(value);
const smooth = (value: number) => { const t = clampSurf(value); return t ** 3 * (10 - 15 * t + 6 * t * t); };
const angleDifference = (a: number, b: number) => Math.atan2(Math.sin(a - b), Math.cos(a - b));
const hermite = (a: number, b: number, va: number, vb: number, duration: number, t: number) =>
  (2 * t ** 3 - 3 * t * t + 1) * a + (t ** 3 - 2 * t * t + t) * duration * va
  + (-2 * t ** 3 + 3 * t * t) * b + (t ** 3 - t * t) * duration * vb;

/** Illustration gates only. An active animation is never a personal safety clearance. */
export function waterSportHoldReason(state: WaterSportSceneState, sport: WaterSceneSport): string | null {
  if (state.reports?.some((report) => report.kind === 'overflow')) return 'A nearby overflow is reported. Colin waits on shore.';
  if (state.availability !== 'ready' || !finite(state.combinedWaveHeightM) || !finite(state.combinedPeriodS)
    || state.combinedWaveHeightM < 0 || state.combinedPeriodS <= 0
    || !finite(state.wind.speedKmh) || !finite(state.wind.gustKmh) || !finite(state.cleanliness01)) {
    return 'Some sea conditions are unavailable. Colin waits on shore.';
  }
  if (!state.weather.daylight) return 'It is after daylight. Colin rests on shore.';
  if (state.weather.weatherCode >= 95) return 'Storm conditions are forecast. Colin waits on shore.';
  if (state.weather.visibilityM !== null && state.weather.visibilityM < 500) return 'Visibility is very limited. Colin waits on shore.';
  if (state.wind.speedKmh >= 45 || state.wind.gustKmh >= 65 || state.combinedWaveHeightM > 3.5) {
    return 'These conditions exceed this little illustrated session. Colin waits on shore.';
  }
  const current = state.waterSportEvidence?.currentSpeedMs;
  if (finite(current) && current >= (sport === 'swim' ? .5 : .8)) return 'A strong current is forecast. Colin waits on shore.';
  if (sport === 'swim' || sport === 'sup' || sport === 'sup-touring') {
    if (!finite(current) || current < 0) return 'Current information is unavailable. Colin waits on shore.';
    const swimming = sport === 'swim';
    if (state.combinedWaveHeightM > (swimming ? .65 : .95) || state.wind.speedKmh > (swimming ? 25 : 30)
      || state.wind.gustKmh > (swimming ? 38 : 45) || state.cleanliness01 < (swimming ? .4 : .3)) {
      return swimming ? 'The water is too unsettled for this illustrated swim. Colin waits on shore.'
        : 'The water is too unsettled for this illustrated tour. Colin waits on shore.';
    }
  }
  if (sport === 'bodyboarding' || sport === 'sup-surf') {
    if (state.cleanliness01 < .22 || (sport === 'sup-surf' && state.cleanliness01 < .36)) {
      return 'The surf is very disorganised. Colin watches the sets from shore.';
    }
    if (state.combinedWaveHeightM < .35) return 'There is little breaking surf. Colin watches from shore.';
  }
  if (sport === 'sup-downwind') {
    if (!state.waterSportEvidence?.windDirectionKnown || !finite(state.wind.shoreRelativeDeg)) {
      return 'Wind direction is unavailable. Colin waits on shore.';
    }
    const windAngle = angleDifference(state.wind.shoreRelativeDeg * Math.PI / 180, 0);
    // The coastal camera supports a shoreward run, not an invented offshore route.
    if (Math.abs(windAngle) > 25 * Math.PI / 180 + 1e-9) return 'This wind does not support the shoreward run shown here. Colin waits on shore.';
    if (state.wind.speedKmh < 12 || state.combinedWaveHeightM < .2) return 'There is little downwind push. Colin waits on shore.';
    if (state.combinedWaveHeightM > 2 || state.cleanliness01 < .3) return 'The bumps exceed this illustrated downwind run. Colin waits on shore.';
  }
  return null;
}

type PathPoint = { along: number; progress: number };
type Choreography = {
  clip: WaterSportClip; stage: WaterSportStage; description: string; pose01: number;
  heading?: number; opacity: number; path: (phase: number) => PathPoint; phase: number; cycle: number;
};

function riderChoreography(state: WaterSportSceneState, sport: WaterSceneSport, set: ReturnType<typeof surfSetAt>): Choreography {
  const p = set.phase, bodyboard = sport === 'bodyboarding';
  const end = .05 + .60 * 1.15 + SURF_FACE_OFFSET;
  const stopped = end + 1.15 * .07 / 2;
  const shoreA = surfShorePoint(.63, state.tide.position01, state.coastGeometry);
  const shoreB = surfShorePoint(.73, state.tide.position01, state.coastGeometry);
  const coastScale = Math.min(1, 65 / Math.hypot(shoreB.x - shoreA.x, shoreB.y - shoreA.y));
  const carve = (.006 + .047 * easeSurf((state.combinedWaveHeightM - .5) / 2)) * state.cleanliness01 ** 2 * coastScale;
  const direction = state.surfDirection === 'right' ? 1 : -1;
  const path = (phase: number): PathPoint => {
    let progress = SURF_LINEUP.progress;
    if (phase >= .20 && phase < .34) progress = hermite(progress, .05 + .34 * 1.15 + SURF_FACE_OFFSET, 0, 1.15, .14, clampSurf((phase - .20) / .14));
    else if (phase >= .34 && phase < .60) progress = .05 + phase * 1.15 + SURF_FACE_OFFSET;
    else if (phase >= .60 && phase < .67) progress = hermite(end, stopped, 1.15, 0, .07, clampSurf((phase - .60) / .07));
    else if (phase >= .67 && phase < .75) progress = stopped;
    else if (phase >= .75 && phase < .95) progress = mix(stopped, SURF_LINEUP.progress, easeSurf((phase - .75) / .20));
    return { progress, along: SURF_LINEUP.along + direction * carve * (smooth((phase - .34) / .26) - smooth((phase - .75) / .20)) };
  };
  let clip: WaterSportClip = bodyboard ? 'bodyboard-ride' : 'sup-brace';
  let stage: WaterSportStage = 'waiting';
  let description = bodyboard ? 'Colin waits on his bodyboard for the next set.' : 'Colin stands ready for the next set.';
  let heading: number | undefined = SURF_HEADING + Math.PI;
  let pose01 = 0;
  if (p >= .14 && p < .20) {
    clip = bodyboard ? 'bodyboard-turn' : 'sup-turn'; stage = 'turning'; pose01 = 1 - easeSurf((p - .14) / .06);
    heading = SURF_HEADING + Math.PI * pose01; description = 'Colin turns towards the approaching set.';
  } else if (p >= .20 && p < .34) {
    clip = bodyboard ? 'bodyboard-paddle' : 'sup-paddle'; stage = 'paddling'; heading = undefined;
    description = bodyboard ? 'Colin kicks and paddles to catch the approaching wave.' : 'Colin paddles into the approaching wave, already standing.';
  } else if (p >= .34 && p < .60) {
    clip = bodyboard ? 'bodyboard-trim' : 'sup-trim'; stage = 'riding'; heading = undefined;
    description = bodyboard ? 'Colin stays prone, trimming along the same breaking wave.' : 'Colin bends his knees and trims along the breaking wave.';
  } else if (p >= .60 && p < .67) {
    clip = bodyboard ? 'bodyboard-ride' : 'sup-brace'; stage = 'gliding'; heading = undefined;
    description = 'The wave eases. Colin finishes the ride.';
  } else if (p >= .67 && p < .75) {
    clip = bodyboard ? 'bodyboard-turn' : 'sup-turn'; stage = 'turning'; pose01 = easeSurf((p - .67) / .08);
    heading = SURF_HEADING + Math.PI * pose01; description = 'Colin turns back towards the line-up.';
  } else if (p >= .75 && p < .95) {
    clip = bodyboard ? 'bodyboard-paddle' : 'sup-paddle'; stage = 'paddling'; heading = undefined;
    description = bodyboard ? 'Colin kicks and paddles back out, nose first.' : 'Colin paddles back out, nose first.';
  }
  return { clip, stage, description, heading, pose01, opacity: 1, path, phase: p, cycle: surfCycleSeconds(state) };
}

function transectChoreography(state: WaterSportSceneState, elapsed: number, sport: WaterSceneSport): Choreography {
  const swimming = sport === 'swim';
  const roughness = 1 - state.cleanliness01;
  const cycle = (swimming ? 58 : 44) * (1 + roughness * .35 + (state.waterSportEvidence?.currentSpeedMs ?? 0) * .3);
  const p = fract(elapsed / cycle), far = swimming ? .50 : .34, near = swimming ? .69 : .59;
  const path = (phase: number): PathPoint => {
    let progress = phase < .42 ? mix(far, near, easeSurf(phase / .42)) : phase < .50 ? near
      : phase < .92 ? mix(near, far, easeSurf((phase - .50) / .42)) : far;
    const local = phase < .42 ? clampSurf(phase / .42) : clampSurf((phase - .50) / .42);
    const active = phase < .42 || (phase >= .50 && phase < .92);
    if (active) {
      const seconds = (Math.floor(elapsed / cycle) + phase) * cycle;
      const stroke = seconds * (swimming ? .74 : .58) * (1 + roughness * .13) * (swimming ? 2 : 1);
      // Each pull adds a small surge followed by a glide. The envelope vanishes
      // at both turn points, preserving position and velocity continuity.
      const pulse = (swimming ? .00028 : .0011) * (1 - Math.cos(stroke * Math.PI * 2)) * Math.sin(local * Math.PI) ** 2;
      progress += (phase < .42 ? 1 : -1) * pulse;
    }
    return { along: swimming ? .76 : .70, progress };
  };
  const turningOut = p >= .42 && p < .50, turningIn = p >= .92;
  const pose01 = turningOut ? easeSurf((p - .42) / .08) : turningIn ? 1 - easeSurf((p - .92) / .08) : 0;
  const turning = turningOut || turningIn;
  const clip: WaterSportClip = turning ? (swimming ? 'swim-turn' : 'sup-turn') : swimming ? 'swim-crawl'
    : roughness > .46 ? 'sup-kneel' : 'sup-paddle';
  return { clip, stage: turning ? 'turning' : swimming ? 'swimming' : 'paddling', pose01,
    heading: turning ? SURF_HEADING + Math.PI * pose01 : p < .42 ? SURF_HEADING : SURF_HEADING + Math.PI,
    description: turning ? swimming ? 'Colin treads water and turns for the return.' : 'Colin turns the board before the return.'
      : swimming ? 'Colin swims a short out-and-back with steady crawl strokes.'
        : roughness > .46 ? 'Colin kneels to paddle through the small chop.' : 'Colin paddles a steady out-and-back tour.',
    opacity: 1, path, phase: p, cycle };
}

function downwindChoreography(state: WaterSportSceneState, set: ReturnType<typeof surfSetAt>): Choreography {
  const p = set.phase;
  const angle = angleDifference(state.wind.shoreRelativeDeg * Math.PI / 180, 0);
  const centreAlong = .65 + Math.min(0, Math.sin(angle)) * .2;
  const shoreA = surfShorePoint(centreAlong - .005, state.tide.position01, state.coastGeometry);
  const shoreB = surfShorePoint(centreAlong + .005, state.tide.position01, state.coastGeometry);
  const shoreDx = (shoreB.x - shoreA.x) / .01, shoreDy = (shoreB.y - shoreA.y) / .01;
  const targetTangent = Math.tan(SURF_HEADING + angle);
  const alongSlope = (targetTangent * 600 - 220) / (shoreDy - targetTangent * shoreDx);
  const centre = surfWaterPoint(centreAlong, .55, state.tide.position01, state.coastGeometry);
  // Quartering runs need a little more room at either end of the curved bay.
  const margin = .07 * easeSurf((Math.abs(angle) * 180 / Math.PI - 20) / 10);
  const path = (phase: number): PathPoint => {
    const catchProgress = .05 + .24 * 1.15 + .07;
    const finishProgress = .05 + .53 * 1.15 + .07;
    // A complete shoreward pass: paddle onto the bump, match its actual
    // progress while gliding, then decelerate into the sheltered foreground.
    // Hermite joins preserve velocity at both changes of activity.
    const finalProgress = .91 - margin, finishPaddle = finalProgress - .16 * .27;
    const progress = phase < .24 ? hermite(.17 + margin, catchProgress, .7, 1.15, .24, clampSurf(phase / .24))
      : phase < .53 ? .05 + phase * 1.15 + .07
        : phase < .73 ? hermite(finishProgress, finishPaddle, 1.15, .16, .20, clampSurf((phase - .53) / .20))
          : finishPaddle + .16 * clampSurf(phase - .73, 0, .27);
    let along = centreAlong + (progress - .55) * alongSlope;
    // Invert the curved coast so the run keeps the same wind-relative heading.
    // Five local Newton steps are bounded and independent of animation duration.
    for (let i = 0; i < 5; i++) {
      const point = surfWaterPoint(along, progress, state.tide.position01, state.coastGeometry);
      const a = surfShorePoint(along - .0001, state.tide.position01, state.coastGeometry);
      const b = surfShorePoint(along + .0001, state.tide.position01, state.coastGeometry);
      const derivative = ((b.y - a.y) - targetTangent * (b.x - a.x)) / .0002;
      if (Math.abs(derivative) < .001) break;
      along = clampSurf(along - (point.y - centre.y - targetTangent * (point.x - centre.x)) / derivative, .2, .95);
    }
    return { progress, along };
  };
  const gliding = p >= .24 && p < .53;
  return { clip: gliding ? 'sup-trim' : 'sup-paddle', stage: gliding ? 'gliding' : 'paddling', pose01: 0,
    description: p < .03 ? 'Another downwind run begins.' : p > .97 ? 'Colin finishes this downwind run.'
      : gliding ? 'Colin glides shorewards with the wind on a passing bump.'
        : p < .24 ? 'Colin paddles with the wind to catch the approaching bump.' : 'Colin paddles the final stretch towards shore.',
    opacity: easeSurf(p / .03) * (1 - easeSurf((p - .97) / .03)), path, phase: p, cycle: surfCycleSeconds(state) };
}

/** The actor samples the exact set surface used by the water renderer. No separate bobbing ocean. */
export function waterSportMotion(state: WaterSportSceneState, elapsed: number, sport: WaterSceneSport,
  set = surfSetAt(state, finite(elapsed) ? Math.max(0, elapsed) : 0)): WaterSportMotion {
  const time = finite(elapsed) ? Math.max(0, elapsed) : 0;
  const holdReason = waterSportHoldReason(state, sport);
  if (holdReason) {
    const shore = surfShorePoint(.68, Math.max(.5, state.tide.position01), state.coastGeometry);
    return { clip: 'shore', stage: 'resting', pose01: fract(time / 3), stroke01: 0, strokePower01: 0,
      x: Math.min(944, shore.x + 55), y: shore.y, along: .68, progress: 1, heading: SURF_HEADING,
      rotation: 0, scale: .78, opacity: 1, speedPxS: 0, riding: false, onWater: false, description: holdReason, holdReason };
  }
  const movement = sport === 'bodyboarding' || sport === 'sup-surf' ? riderChoreography(state, sport, set)
    : sport === 'sup-downwind' ? downwindChoreography(state, set) : transectChoreography(state, time, sport);
  const position = movement.path(movement.phase);
  const before = movement.path(movement.phase - .00001), after = movement.path(movement.phase + .00001);
  const flatBefore = surfWaterPoint(before.along, before.progress, state.tide.position01, state.coastGeometry);
  const flatAfter = surfWaterPoint(after.along, after.progress, state.tide.position01, state.coastGeometry);
  const dx = flatAfter.x - flatBefore.x, dy = flatAfter.y - flatBefore.y;
  const distance = Math.hypot(dx, dy);
  const heading = movement.heading ?? (distance > 1e-8 ? Math.atan2(dy, dx) : SURF_HEADING);
  const point = surfSetSurfacePoint(set.waves, position.along, position.progress, state.tide.position01, state.coastGeometry);
  // Project the local water slope along travel. Turning poses pivot in place.
  const step = distance > 1e-8 ? .04 / Math.max(.001, Math.abs(after.progress - before.progress)) : 0;
  const alongStep = distance > 1e-8 ? (after.along - before.along) * step : 0;
  const progressStep = distance > 1e-8 ? (after.progress - before.progress) * step : .04;
  const front = surfSetSurfacePoint(set.waves, position.along + alongStep, position.progress + progressStep, state.tide.position01, state.coastGeometry);
  const back = surfSetSurfacePoint(set.waves, position.along - alongStep, position.progress - progressStep, state.tide.position01, state.coastGeometry);
  const surfaceAngle = Math.atan2(front.y - back.y, front.x - back.x);
  const flatHeading = distance > 1e-8 ? Math.atan2(dy, dx) : SURF_HEADING;
  const activeStroke = movement.stage === 'paddling' || movement.stage === 'swimming';
  const cadence = sport === 'swim' ? .74 : sport === 'bodyboarding' ? 1.05 : .58;
  const stroke01 = fract(time * cadence * (1 + (1 - state.cleanliness01) * .13));
  const power = activeStroke ? Math.max(0, Math.sin(stroke01 * Math.PI * 2)) : 0;
  return { ...position, ...point, heading, clip: movement.clip, stage: movement.stage,
    pose01: movement.stage === 'turning' ? movement.pose01 : activeStroke ? stroke01 : 0,
    stroke01, strokePower01: power, rotation: clampSurf(angleDifference(surfaceAngle, flatHeading), -.32, .32),
    scale: .8 + (position.progress - SURF_LINEUP.progress) * .35, opacity: movement.opacity,
    speedPxS: distance / (.00002 * movement.cycle), riding: movement.stage === 'riding' || (sport === 'sup-downwind' && movement.stage === 'gliding'),
    onWater: true, description: movement.description, holdReason: null };
}
